import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, DoorOpen, Loader2, Wrench, Sparkles } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Loading } from "@/components/site/ui";
import { Panel, AdminHeading, adminInput } from "@/components/admin/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS_COLOR = {
  Available: "text-green-300 bg-green-400/10", Ready: "text-green-300 bg-green-400/10",
  Reserved: "text-pool bg-pool/10", Occupied: "text-gold bg-gold/10",
  Dirty: "text-amber-300 bg-amber-400/10", "Cleaning Assigned": "text-amber-300 bg-amber-400/10",
  "Cleaning in Progress": "text-amber-300 bg-amber-400/10",
  "Inspection Required": "text-pool bg-pool/10", "Reclean Required": "text-red-300 bg-red-400/10",
  "Maintenance Required": "text-red-300 bg-red-400/10", "Out of Service": "text-red-300 bg-red-400/10",
  Blocked: "text-muted-foreground bg-white/5",
};
const UNIT_STATUSES = ["Available", "Reserved", "Occupied", "Dirty", "Cleaning Assigned",
  "Cleaning in Progress", "Inspection Required", "Reclean Required", "Inspected", "Ready",
  "Maintenance Required", "Out of Service", "Blocked"];

export default function AdminRooms() {
  const [units, setUnits] = useState(null);
  const [f, setF] = useState("");
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState({ name: "", unit_type: "Custom Unit", capacity: 2 });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/accommodations", { params: f ? { status: f } : {} })
    .then((r) => setUnits(r.data)).catch(() => setUnits([]));
  useEffect(() => { load(); }, [f]);

  const create = async () => {
    setSaving(true);
    try { await api.post("/admin/accommodations", form); toast.success("Unit created."); setDlg(false); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const setStatus = async (u, status) => {
    try { await api.post(`/admin/accommodations/${u.id}/status`, { status }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const statuses = ["", ...UNIT_STATUSES];
  return (
    <div>
      <AdminHeading title="Rooms & Readiness" subtitle={`${units?.length ?? 0} accommodation units`}>
        <button onClick={() => setDlg(true)} className="btn-gold px-4 py-2.5 text-sm flex items-center gap-2" data-testid="room-add-btn"><Plus size={15} /> New Unit</button>
      </AdminHeading>
      <div className="flex gap-2 mb-5 flex-wrap">
        {statuses.map((s) => (
          <button key={s} onClick={() => setF(s)} className={`px-3 py-1.5 rounded-full text-xs border ${f === s ? "bg-pool/15 border-pool/40 text-pool" : "border-white/10 text-muted-foreground"}`}>{s || "All"}</button>
        ))}
      </div>
      {units === null ? <Loading /> : units.length === 0 ? <Panel><p className="text-sm text-muted-foreground py-6 text-center">No units for this filter.</p></Panel> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="rooms-board">
          {units.map((u) => (
            <div key={u.id} className="glass rounded-xl p-5" data-testid={`room-${u.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-serif text-xl text-foreground flex items-center gap-2"><DoorOpen size={16} className="text-gold" /> {u.name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{u.unit_type} · cap {u.capacity}</p>
                </div>
                <span className={`text-[11px] px-2 py-0.5 rounded-full ${STATUS_COLOR[u.status] || "text-muted-foreground bg-white/5"}`}>{u.status}</span>
              </div>
              <div className="flex items-center gap-3 mt-3 text-[11px] text-muted-foreground">
                <span className="flex items-center gap-1"><Sparkles size={12} /> {u.housekeeping_status}</span>
                <span className="flex items-center gap-1"><Wrench size={12} /> {u.maintenance_status}</span>
              </div>
              {u.current_guest && <p className="text-xs text-gold mt-2">Guest: {u.current_guest}</p>}
              <select className={`${adminInput} mt-3 text-xs`} value={u.status} onChange={(e) => setStatus(u, e.target.value)} data-testid={`room-status-${u.id}`}>
                {UNIT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          ))}
        </div>
      )}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="bg-surface border-white/10 max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-2xl">New Accommodation Unit</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input className={adminInput} placeholder="Unit name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="room-name" />
            <div className="grid grid-cols-2 gap-3">
              <input className={adminInput} placeholder="Type" value={form.unit_type} onChange={(e) => setForm({ ...form, unit_type: e.target.value })} />
              <input type="number" className={adminInput} placeholder="Capacity" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: +e.target.value })} />
            </div>
            <button onClick={create} disabled={saving || !form.name} className="btn-gold w-full py-2.5 text-sm flex items-center justify-center gap-2" data-testid="room-save">
              {saving ? <Loader2 className="animate-spin" size={15} /> : <Plus size={15} />} Create Unit
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
